import { useState, useMemo } from 'react'
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription 
} from '../ui/card'
import { ScrollArea } from '../ui/scroll-area'
import { 
  Calendar as CalendarIcon, 
  Clock, 
  ChevronRight, 
  ChevronLeft, 
  Filter 
} from 'lucide-react'
import { Button } from '../ui/button'
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from '../ui/popover'
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select'
import { cn } from '../../lib/utils'
import { 
  format, 
  addDays, 
  addWeeks, 
  addMonths, 
  startOfDay, 
  endOfDay, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfMonth, 
  isWithinInterval 
} from 'date-fns'

// Explicitly define Visit type with strict status
interface Visit {
  id: string
  patient: { name: string }
  startTime: string
  type: string
  status: 'upcoming' | 'in-progress' | 'completed'
}

// Reuse existing status styles from schedule-card
const statusStyles = {
  'upcoming': {
    bg: 'bg-blue-50/80 dark:bg-blue-950/50',
    border: 'border-blue-100 dark:border-blue-900/50',
    text: 'text-blue-900 dark:text-blue-100',
    indicator: 'bg-blue-500 dark:bg-blue-400',
    hover: 'hover:bg-blue-100/80 dark:hover:bg-blue-900/50'
  },
  'in-progress': {
    bg: 'bg-emerald-50/80 dark:bg-emerald-950/50',
    border: 'border-emerald-100 dark:border-emerald-900/50',
    text: 'text-emerald-900 dark:text-emerald-100',
    indicator: 'bg-emerald-500 dark:bg-emerald-400',
    hover: 'hover:bg-emerald-100/80 dark:hover:bg-emerald-900/50'
  },
  'completed': {
    bg: 'bg-gray-50/80 dark:bg-gray-900/50',
    border: 'border-gray-100 dark:border-gray-800/50',
    text: 'text-gray-900 dark:text-gray-100',
    indicator: 'bg-gray-500 dark:bg-gray-400',
    hover: 'hover:bg-gray-100/80 dark:hover:bg-gray-800/50'
  }
}

type ViewMode = 'day' | 'week' | 'month'
type VisitStatus = 'upcoming' | 'in-progress' | 'completed'

// Generate mock visits for different dates
const generateMockVisits = (selectedDate: Date, viewMode: ViewMode): Visit[] => {
  const baseVisits: Visit[] = [
    {
      id: '1',
      patient: { name: 'Sarah Johnson' },
      startTime: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 9, 0).toISOString(),
      type: 'Check-up',
      status: 'upcoming'
    },
    {
      id: '2',
      patient: { name: 'Michael Chen' },
      startTime: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 10, 30).toISOString(),
      type: 'Follow-up',
      status: 'in-progress'
    },
    {
      id: '3',
      patient: { name: 'Emily Davis' },
      startTime: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 14, 0).toISOString(),
      type: 'Consultation',
      status: 'upcoming'
    }
  ]

  // Add more visits for week and month views
  if (viewMode === 'week') {
    baseVisits.push(
      {
        id: '4',
        patient: { name: 'John Smith' },
        startTime: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate() + 2, 11, 0).toISOString(),
        type: 'Physical Exam',
        status: 'upcoming'
      },
      {
        id: '5',
        patient: { name: 'Lisa Wong' },
        startTime: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate() + 4, 15, 30).toISOString(),
        type: 'Vaccination',
        status: 'completed'
      }
    )
  }

  if (viewMode === 'month') {
    baseVisits.push(
      ...Array.from({ length: 5 }, (_, i) => ({
        id: `month-${i + 6}`,
        patient: { name: `Patient ${i + 1}` },
        startTime: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate() + (i * 5), 10, 0).toISOString(),
        type: 'Routine Check',
        status: 'upcoming' as const
      }))
    )
  }

  return baseVisits
}

export function EnhancedScheduleCard() {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const [viewMode, setViewMode] = useState<ViewMode>('day')
  const [filterStatuses, setFilterStatuses] = useState<VisitStatus[]>([])

  // Generate mock visits based on selected date and view mode
  const visits = useMemo(() => 
    generateMockVisits(selectedDate, viewMode), 
    [selectedDate, viewMode]
  )

  // Filter visits based on date range and status
  const filteredVisits = useMemo(() => {
    const startRange = 
      viewMode === 'day' ? startOfDay(selectedDate) : 
      viewMode === 'week' ? startOfWeek(selectedDate) : 
      startOfMonth(selectedDate)

    const endRange = 
      viewMode === 'day' ? endOfDay(selectedDate) : 
      viewMode === 'week' ? endOfWeek(selectedDate) : 
      endOfMonth(selectedDate)

    return visits.filter(appt => {
      const visitDate = new Date(appt.startTime)
      const isInDateRange = isWithinInterval(visitDate, { start: startRange, end: endRange })
      const matchesStatusFilter = filterStatuses.length === 0 || filterStatuses.includes(appt.status)

      return isInDateRange && matchesStatusFilter
    })
  }, [visits, selectedDate, viewMode, filterStatuses])

  const navigateDate = (direction: 'prev' | 'next') => {
    let newDate
    switch(viewMode) {
      case 'day':
        newDate = addDays(selectedDate, direction === 'prev' ? -1 : 1)
        break
      case 'week':
        newDate = addWeeks(selectedDate, direction === 'prev' ? -1 : 1)
        break
      case 'month':
        newDate = addMonths(selectedDate, direction === 'prev' ? -1 : 1)
        break
    }
    setSelectedDate(newDate)
  }

  // Format date based on view mode
  const formatDate = () => {
    switch(viewMode) {
      case 'day':
        return format(selectedDate, 'MMMM d, yyyy')
      case 'week':
        return `${format(startOfWeek(selectedDate), 'MMMM d')} - ${format(endOfWeek(selectedDate), 'd, yyyy')}`
      case 'month':
        return format(selectedDate, 'MMMM yyyy')
    }
  }

  return (
    <Card className={cn(
      "schedule-section h-full",
      "bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm",
      "transition-all duration-300 ease-out"
    )}>
      <CardHeader className="border-b border-border/50 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900">
              <CalendarIcon className="h-5 w-5 text-blue-700 dark:text-blue-300" />
            </div>
            <span className="text-lg font-semibold">
              {viewMode === 'day' ? "Today's Schedule" : 
               viewMode === 'week' ? "This Week's Schedule" : 
               "This Month's Schedule"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="mr-2 h-4 w-4" /> Filter
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64">
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <h4 className="font-medium leading-none">Filter Visits</h4>
                    <p className="text-sm text-muted-foreground">
                      Select status to filter visits
                    </p>
                  </div>
                  <div className="grid gap-2">
                    {Object.keys(statusStyles).map(status => (
                      <Button 
                        key={status}
                        variant={filterStatuses.includes(status as VisitStatus) ? 'default' : 'outline'}
                        onClick={() => {
                          setFilterStatuses(prev => {
                            if (prev.includes(status as VisitStatus)) {
                              return prev.filter(s => s !== status)
                            }
                            return [...prev, status as VisitStatus]
                          })
                        }}
                        className="w-full justify-start"
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </Button>
                    ))}
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            <Select 
              value={viewMode} 
              onValueChange={(mode: ViewMode) => setViewMode(mode)}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="View Mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Day</SelectItem>
                <SelectItem value="week">Week</SelectItem>
                <SelectItem value="month">Month</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardTitle>
        <CardDescription className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="icon" 
              onClick={() => navigateDate('prev')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span>
              {formatDate()}
            </span>
            <Button 
              variant="outline" 
              size="icon" 
              onClick={() => navigateDate('next')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <span className="text-sm text-muted-foreground">
            {filteredVisits.length} visits
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-[400px]">
          <div className="p-6">
            {filteredVisits.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            No visits for this period
          </div>
        ) : (
          <div className="space-y-4">
            {filteredVisits.map((visit) => {
              const styles = statusStyles[visit.status]
              return (
                <div 
                  key={visit.id} 
                  className={cn(
                    "group relative flex items-center gap-4 p-4 rounded-xl border",
                    "transition-all duration-300 ease-out",
                    "cursor-pointer",
                    styles.bg,
                    styles.border,
                    styles.text,
                    styles.hover
                  )}
                >
                  <div className={cn(
                    "absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-full",
                    styles.indicator,
                    "transition-all duration-300 ease-out",
                    "group-hover:h-12"
                  )} />
                  
                  <div className="flex items-center gap-2 min-w-[110px] pl-3">
                    <Clock className="h-4 w-4 opacity-80" />
                    <span className="text-sm font-medium">
                      {format(new Date(visit.startTime), 'hh:mm a')}
                    </span>
                  </div>
                  
                  <div className="flex-1">
                    <div className="font-semibold">{visit.patient.name}</div>
                    <div className="text-sm opacity-80">{visit.type}</div>
                  </div>

                  <ChevronRight className={cn(
                    "h-5 w-5 opacity-0 -translate-x-2",
                    "transition-all duration-300 ease-out",
                    "group-hover:opacity-50 group-hover:translate-x-0"
                  )} />
                </div>
              )
            })}
          </div>
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  )
}
